import * as R from 'ramda';

/**
 * Given a T and T[]:
 * - Add the item to the list if it doesn't exist (using R.equals)
 * - Remove the item form the list if it already exists
 *
 * This is usually what you want for lists of checkboxes
 */
export const toggleListItem = <T>(needle: T, haystack: T[]): T[] =>
  R.ifElse(R.includes(needle), R.without([needle]), R.append(needle))(haystack);

export const isNilOrEmpty = R.either(R.isNil, R.isEmpty);

export const unlessNilOrEmpty = <T>(
  fallback: T,
  value: T | null | undefined,
): T => {
  // no boolean check to avoid edge cases with 0, '', etc.
  if (value === null || value === undefined) return fallback;
  if (R.isEmpty(value)) return fallback;
  return value;
};

type SafeParseResult<T> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      error: unknown;
    };

/**
 * Simple wrapper over JSON.parse that envelopes the parse response and provides a first-class API
 *   for handling errors rather than throwing exceptions.
 *
 * Note that this type-casts the return value to T, so you should be sure that the JSON you're
 *  parsing is actually of the type you're expecting.
 *
 * In many cases, you should prefer parsing with a Zod schema or similar, but this is useful for
 *  quick-and-dirty parsing or plugging in to legacy code.
 */
export const jsonSafeParse = <T>(str: string): SafeParseResult<T> => {
  try {
    const data = JSON.parse(str) as T;
    return {success: true, data};
  } catch (e) {
    return {success: false, error: e};
  }
};

/** Coerce an empty array to an empty object, otherwise return the value
 * This is not type-safe so it should be used sparingly within legacy data transformations.
 */
export const objectifyEmptyArray = R.when(
  R.both(R.is(Array), R.isEmpty),
  R.always({}),
);
